import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { musicApi, type WantedAlbum } from "@/api/music";
import type { GrabRecord } from "@/api/types";
import { Button } from "@/components/ui/Button";
import { EmptyState } from "@/components/ui/EmptyState";
import { PageHeader } from "@/components/ui/PageHeader";
import { Skeleton } from "@/components/ui/Skeleton";
import { SearchInput } from "@/components/ui/Field";
import { Status } from "@/components/ui/Status";
import { useToast } from "@/components/ui/Toast";
import { cn } from "@/lib/cn";

/**
 * Albums that are monitored but don't yet have a file in the library.
 * Drives the "Wanted" tab — equivalent of Lidarr's Missing list.
 *
 * Mirrors {@link MoviesWanted} and {@link TVWanted} 1:1 in shape:
 * filterable table + per-row Search & Grab + page-level "Search all"
 * with bounded concurrency. Optimistic UI flips rows to "searching"
 * before the network hop returns so slow indexers still feel snappy.
 */

type RowState =
  | { kind: "idle" }
  | { kind: "searching" }
  | { kind: "grabbed"; record: GrabRecord }
  | { kind: "failed"; reason: string };

export function MusicWanted() {
  const qc = useQueryClient();
  const { toast } = useToast();
  const [searchingAll, setSearchingAll] = useState(false);
  const [progress, setProgress] = useState<{ done: number; total: number }>({
    done: 0,
    total: 0,
  });
  const [filter, setFilter] = useState("");
  const [rowStates, setRowStates] = useState<Record<number, RowState>>({});

  const { data: wanted, isLoading } = useQuery({
    queryKey: ["music", "wanted"],
    queryFn: () => musicApi.wanted(500),
  });

  const grabMut = useMutation({
    mutationFn: (id: number) => musicApi.searchAlbum(id),
  });

  const lower = filter.trim().toLowerCase();
  const filtered = useMemo(() => {
    if (!wanted) return [];
    if (!lower) return wanted;
    return wanted.filter(
      (a) =>
        a.artist_name.toLowerCase().includes(lower) ||
        a.title.toLowerCase().includes(lower),
    );
  }, [wanted, lower]);

  const onGrab = (album: WantedAlbum) => {
    setRowStates((s) => ({ ...s, [album.album_id]: { kind: "searching" } }));
    grabMut.mutate(album.album_id, {
      onSuccess: (rec) => {
        setRowStates((s) => ({
          ...s,
          [album.album_id]: { kind: "grabbed", record: rec },
        }));
        toast(
          `Grabbed ${rec.release_title} via ${rec.indexer_name ?? "?"} → ${rec.download_client_name ?? "?"}`,
          "success",
        );
        qc.invalidateQueries({ queryKey: ["music", "wanted"] });
        qc.invalidateQueries({ queryKey: ["grabs"] });
      },
      onError: (err) => {
        const reason = err instanceof Error ? err.message : "Search failed";
        setRowStates((s) => ({
          ...s,
          [album.album_id]: { kind: "failed", reason },
        }));
        toast(reason, "error");
      },
    });
  };

  const searchAll = async () => {
    if (!filtered || filtered.length === 0) return;
    if (
      !confirm(
        `Search ${filtered.length} monitored album${filtered.length === 1 ? "" : "s"} across your indexers? This may take several minutes.`,
      )
    ) {
      return;
    }
    setSearchingAll(true);
    setProgress({ done: 0, total: filtered.length });
    let succeeded = 0;
    let failed = 0;

    const CONCURRENCY = 3;
    const queue = filtered.slice();
    const workers = Array.from({ length: CONCURRENCY }, async () => {
      while (queue.length > 0) {
        const album = queue.shift();
        if (!album) return;
        setRowStates((s) => ({
          ...s,
          [album.album_id]: { kind: "searching" },
        }));
        try {
          const rec = await musicApi.searchAlbum(album.album_id);
          setRowStates((s) => ({
            ...s,
            [album.album_id]: { kind: "grabbed", record: rec },
          }));
          succeeded += 1;
        } catch (err) {
          const reason = err instanceof Error ? err.message : "failed";
          setRowStates((s) => ({
            ...s,
            [album.album_id]: { kind: "failed", reason },
          }));
          failed += 1;
        } finally {
          setProgress((p) => ({ ...p, done: p.done + 1 }));
        }
      }
    });
    await Promise.all(workers);
    setSearchingAll(false);
    qc.invalidateQueries({ queryKey: ["music", "wanted"] });
    qc.invalidateQueries({ queryKey: ["artists"] });
    qc.invalidateQueries({ queryKey: ["grabs"] });
    toast(
      `Searched ${filtered.length} album${filtered.length === 1 ? "" : "s"} — ${succeeded} grabbed, ${failed} no match / failed`,
      succeeded > 0 ? "success" : "info",
    );
  };

  return (
    <div className="space-y-6">
      <PageHeader
        eyebrow="Wanted"
        title="Albums missing files"
        description="Monitored albums without a copy in your library. Search per row, or batch-search the whole list."
        actions={
          <Button
            onClick={searchAll}
            loading={searchingAll}
            disabled={!filtered || filtered.length === 0}
          >
            {searchingAll
              ? `Searching ${progress.done}/${progress.total}`
              : "Search all"}
          </Button>
        }
      />

      <div className="flex items-center justify-between gap-4">
        <div className="w-full max-w-sm">
          <SearchInput
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            placeholder="Filter by artist or album"
          />
        </div>
        {wanted && (
          <span className="font-mono text-xs text-foreground/55">
            {filter
              ? `${filtered.length} of ${wanted.length}`
              : `${wanted.length} wanted`}
          </span>
        )}
      </div>

      {isLoading ? (
        <Skeleton.Rows count={8} />
      ) : !wanted || wanted.length === 0 ? (
        <EmptyState
          glyph="cassette"
          title="Nothing wanted"
          description="Every monitored album has a file. Add more artists or unmonitor some."
          action={
            <Link to="/music">
              <Button variant="secondary">Back to artists</Button>
            </Link>
          }
        />
      ) : filtered.length === 0 ? (
        <EmptyState
          glyph="search"
          title="No matches"
          description={`Nothing matches "${filter}".`}
          action={
            <Button variant="ghost" onClick={() => setFilter("")}>
              Clear filter
            </Button>
          }
        />
      ) : (
        <div className="overflow-x-auto rounded-md border border-line bg-surface/30">
          <table className="w-full text-sm">
            <thead className="bg-surface-2/50">
              <tr className="text-left">
                <th className="w-16 px-3 py-2.5"></th>
                <th className="px-3 py-2.5 font-mono text-[10px] font-medium uppercase tracking-[0.12em] text-foreground/55">
                  Artist
                </th>
                <th className="px-3 py-2.5 font-mono text-[10px] font-medium uppercase tracking-[0.12em] text-foreground/55">
                  Album
                </th>
                <th className="px-3 py-2.5 text-right font-mono text-[10px] font-medium uppercase tracking-[0.12em] text-foreground/55">
                  Year
                </th>
                <th className="px-3 py-2.5 font-mono text-[10px] font-medium uppercase tracking-[0.12em] text-foreground/55">
                  Type
                </th>
                <th className="px-3 py-2.5 font-mono text-[10px] font-medium uppercase tracking-[0.12em] text-foreground/55">
                  Status
                </th>
                <th className="w-32 px-3 py-2.5"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-line/60">
              {filtered.map((a) => (
                <WantedRow
                  key={a.album_id}
                  album={a}
                  state={rowStates[a.album_id] ?? { kind: "idle" }}
                  onGrab={() => onGrab(a)}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function WantedRow({
  album,
  state,
  onGrab,
}: {
  album: WantedAlbum;
  state: RowState;
  onGrab: () => void;
}) {
  const dim = state.kind === "grabbed";
  // Album cover preferred; fall back to artist image so the row isn't
  // a blank rectangle for catalogue albums without dedicated cover art
  // (common for older / niche releases).
  const cover = album.poster_url ?? album.artist_poster_url;
  const year = parseYear(album.release_date);
  return (
    <tr
      className={cn(
        "group transition hover:bg-surface-2/40",
        dim && "opacity-60",
      )}
    >
      <td className="px-3 py-2">
        {cover ? (
          <img
            src={cover}
            alt=""
            className="aspect-square w-12 rounded border border-line object-cover"
            loading="lazy"
          />
        ) : (
          <div className="aspect-square w-12 rounded border border-line bg-surface-2/40" />
        )}
      </td>
      <td className="px-3 py-2.5">
        <Link
          to="/music/artists/$id"
          params={{ id: String(album.artist_id) }}
          className="font-display text-sm font-medium tracking-display-tight hover:text-accent"
        >
          {album.artist_name}
        </Link>
      </td>
      <td className="px-3 py-2.5">
        <Link
          to="/music/albums/$id"
          params={{ id: String(album.album_id) }}
          className="font-display text-base font-medium tracking-display-tight hover:text-accent"
        >
          {album.title}
        </Link>
        {album.disambiguation && (
          // MusicBrainz disambiguation surfaced alongside the year +
          // primary_type so two same-titled albums (e.g. "Kid A"
          // studio vs. "Kid A Mnesia" compilation) read distinctly
          // in the wanted/add list.
          <div className="mt-0.5 text-xs italic text-foreground/55">
            {album.disambiguation}
          </div>
        )}
      </td>
      <td className="px-3 py-2.5 text-right font-mono text-xs tabular-nums text-foreground/65">
        {year ?? "—"}
      </td>
      <td className="px-3 py-2.5 font-mono text-xs uppercase tracking-[0.08em] text-foreground/65">
        {album.primary_type ?? "—"}
      </td>
      <td className="px-3 py-2.5">
        <RowStatus state={state} />
      </td>
      <td className="space-x-2 px-3 py-2 text-right whitespace-nowrap">
        {state.kind === "grabbed" ? (
          <span className="font-mono text-[10px] text-foreground/45">
            {state.record.indexer_name ?? "?"}
          </span>
        ) : (
          <Button
            size="sm"
            variant="secondary"
            loading={state.kind === "searching"}
            onClick={onGrab}
          >
            {state.kind === "failed" ? "Retry" : "Search"}
          </Button>
        )}
      </td>
    </tr>
  );
}

function RowStatus({ state }: { state: RowState }) {
  switch (state.kind) {
    case "idle":
      return (
        <Status variant="chip" tone="accent">
          wanted
        </Status>
      );
    case "searching":
      return (
        <Status variant="chip" tone="info" pulse>
          searching
        </Status>
      );
    case "grabbed":
      return (
        <Status variant="chip" tone="good">
          grabbed
        </Status>
      );
    case "failed":
      return (
        <Status variant="chip" tone="bad">
          {state.reason.length > 22 ? "failed" : state.reason}
        </Status>
      );
  }
}

/** Pull the YYYY out of an ISO-ish date. Returns `null` for null/invalid. */
function parseYear(date: string | null): number | null {
  if (!date) return null;
  const m = /^(\d{4})/.exec(date);
  return m ? Number(m[1]) : null;
}
